import { call, cancel, fork, put } from 'redux-saga/effects';
import { toast } from 'react-toastify';

import * as actions from '../actions';

import { TEXTS } from '../../constants/languages';
import { toastOptionsSuccess, toastOptionsError } from '../../styles/toast';

import TaskSingle from '../../models/tasks/TaskSingle';
import TaskRegular from '../../models/tasks/TaskRegular';
import TaskDelayed from '../../models/tasks/TaskDelayed';
import TaskGlobal from '../../models/tasks/TaskGlobal';

import { convertTasksStructure /* checkTasksNumberLimit */ } from '../../utils/taskHelper';
import { delayGenerator } from '../../utils/utils';

import * as localStorageService from '../../services/localStorage';

import { fetchRequest } from '../../server';
import {
  API_URL,
  API_PATH_TASKS,
  API_PATH_TASKS_GLOBAL,
  API_PATH_TASKS_REGULAR,
  API_PATH_TASKS_SINGLE,
  API_PATH_TASKS_DELAYED,
} from '../../constants/server';

const language = localStorageService.getLanguage();

// saga single task

export function* createTaskSingleSaga(action) {
  try {
    yield put(actions.createTaskSingleStart());

    const tasksStructure = yield call(
      fetchRequest,
      `${API_URL}${API_PATH_TASKS_SINGLE}`,
      'POST',
      JSON.stringify(new TaskSingle(action.payload.task).getCreateTaskSingleDto()),
    );

    yield put(actions.createTaskSingleSuccess());
    yield put(actions.getTasksServerSuccess(convertTasksStructure(tasksStructure.data)));

    toast(TEXTS[language].TASK_CREATED, toastOptionsSuccess);
  } catch (error) {
    toast(error.message, toastOptionsError);
    yield put(actions.createTaskSingleFail(error));
  }
}

// saga regular  task

export function* createTaskRegularSaga(action) {
  try {
    yield put(actions.createTaskRegularStart());

    const tasksStructure = yield call(
      fetchRequest,
      `${API_URL}${API_PATH_TASKS_REGULAR}`,
      'POST',
      JSON.stringify(new TaskRegular(action.payload.task).getCreateTaskRegularDto({ isTasksSingleTitlesUpdated: false })),
    );

    yield put(actions.createTaskRegularSuccess());
    yield put(actions.getTasksServerSuccess(convertTasksStructure(tasksStructure.data)));

    toast(TEXTS[language].TASK_CREATED, toastOptionsSuccess);
  } catch (error) {
    toast(error.message, toastOptionsError);
    yield put(actions.createTaskRegularFail(error));
  }
}

// saga delay task

export function* createTaskDelayedSaga(action) {
  try {
    yield put(actions.createTaskDelayedStart());

    const tasksStructure = yield call(
      fetchRequest,
      `${API_URL}${API_PATH_TASKS_DELAYED}`,
      'POST',
      JSON.stringify(new TaskDelayed(action.payload.task).getCreateTaskDelayedDto()),
    );

    yield put(actions.createTaskDelayedSuccess());
    yield put(actions.getTasksServerSuccess(convertTasksStructure(tasksStructure.data)));

    toast(action.payload.task.singleTaskId ? TEXTS[language].TASK_TRANSFERRED_TO_DELAYED : TEXTS[language].TASK_CREATED, toastOptionsSuccess);
  } catch (error) {
    toast(error.message, toastOptionsError);
    yield put(actions.createTaskDelayedFail(error));
  }
}

// saga global task

export function* createTaskGlobalSaga(action) {
  try {
    yield put(actions.createTaskGlobalStart());

    const tasksStructure = yield call(
      fetchRequest,
      `${API_URL}${API_PATH_TASKS_GLOBAL}`,
      'POST',
      JSON.stringify(new TaskGlobal(action.payload.task).getCreateTaskGlobalDto()),
    );

    yield put(actions.createTaskGlobalSuccess());
    yield put(actions.getTasksServerSuccess(convertTasksStructure(tasksStructure.data)));

    toast(TEXTS[language].GOAL_CREATED, toastOptionsSuccess);
  } catch (error) {
    toast(error.message, toastOptionsError);
    yield put(actions.createTaskGlobalFail(error));
  }
}

// / update single task

export function* updateTaskSingleSaga(action) {
  try {
    const updateTaskSingleStart = yield fork(delayGenerator, action.payload.loadingDelay || 0, actions.updateTaskSingleStart);

    // task check
    if (!action.payload.task.id) {
      throw new Error('No task');
    }

    const tasksStructure = yield call(
      fetchRequest,
      `${API_URL}${API_PATH_TASKS_SINGLE}/${action.payload.task.id}`,
      'PATCH',
      JSON.stringify(new TaskSingle(action.payload.task).getCreateTaskSingleDto()),
    );

    yield cancel(updateTaskSingleStart);

    yield put(actions.updateTaskSingleSuccess());
    yield put(actions.getTasksServerSuccess(convertTasksStructure(tasksStructure.data)));
  } catch (error) {
    toast(error.message, toastOptionsError);
    yield put(actions.updateTaskSingleFail(error));
  }
}

// / update regular task

export function* updateTaskRegularSaga(action) {
  try {
    const updateTaskRegularStart = yield fork(delayGenerator, action.payload.loadingDelay || 0, actions.updateTaskRegularStart);

    // task check
    if (!action.payload.task.id) {
      throw new Error('No task');
    }

    const tasksStructure = yield call(
      fetchRequest,
      `${API_URL}${API_PATH_TASKS_REGULAR}/${action.payload.task.id}`,
      'PATCH',
      JSON.stringify(
        new TaskRegular(action.payload.task).getCreateTaskRegularDto({ isTasksSingleTitlesUpdated: action.payload.isTasksSingleTitlesUpdated }),
      ),
    );

    yield cancel(updateTaskRegularStart);

    yield put(actions.updateTaskRegularSuccess());
    yield put(actions.getTasksServerSuccess(convertTasksStructure(tasksStructure.data)));
  } catch (error) {
    toast(error.message, toastOptionsError);
    yield put(actions.updateTaskRegularFail(error));
  }
}

// / update Delayed task

export function* updateTaskDelayedSaga(action) {
  try {
    const updateTaskDelayedStart = yield fork(delayGenerator, action.payload.loadingDelay || 0, actions.updateTaskDelayedStart);

    // task check
    if (!action.payload.task.id) {
      throw new Error('No task');
    }

    const tasksStructure = yield call(
      fetchRequest,
      `${API_URL}${API_PATH_TASKS_DELAYED}/${action.payload.task.id}`,
      'PATCH',
      JSON.stringify(new TaskDelayed(action.payload.task).getCreateTaskDelayedDto()),
    );

    yield cancel(updateTaskDelayedStart);

    yield put(actions.updateTaskDelayedSuccess());
    yield put(actions.getTasksServerSuccess(convertTasksStructure(tasksStructure.data)));
  } catch (error) {
    toast(error.message, toastOptionsError);
    yield put(actions.updateTaskDelayedFail(error));
  }
}

// / update Global task

export function* updateTaskGlobalSaga(action) {
  try {
    const updateTaskGlobalStart = yield fork(delayGenerator, action.payload.loadingDelay || 0, actions.updateTaskGlobalStart);

    // task check
    if (!action.payload.task.id) {
      throw new Error('No task');
    }

    const tasksStructure = yield call(
      fetchRequest,
      `${API_URL}${API_PATH_TASKS_GLOBAL}/${action.payload.task.id}`,
      'PATCH',
      JSON.stringify(new TaskGlobal(action.payload.task).getCreateTaskGlobalDto()),
    );

    yield cancel(updateTaskGlobalStart);

    yield put(actions.updateTaskGlobalSuccess());
    yield put(actions.getTasksServerSuccess(convertTasksStructure(tasksStructure.data)));
  } catch (error) {
    toast(error.message, toastOptionsError);
    yield put(actions.updateTaskGlobalFail(error));
  }
}

// / remove single task

export function* removeTaskSingleSaga(action) {
  try {
    yield put(actions.removeTaskSingleStart());

    // task check
    if (!action.payload.id) {
      throw new Error('No task');
    }

    const tasksStructure = yield call(fetchRequest, `${API_URL}${API_PATH_TASKS_SINGLE}/${action.payload.id}`, 'DELETE');

    yield put(actions.removeTaskSingleSuccess());
    yield put(actions.getTasksServerSuccess(convertTasksStructure(tasksStructure.data)));

    toast(TEXTS[language].TASK_DELETED, toastOptionsSuccess);
  } catch (error) {
    toast(error.message, toastOptionsError);
    yield put(actions.removeTaskSingleFail(error));
  }
}

// / remove regular task

export function* removeTaskRegularSaga(action) {
  try {
    yield put(actions.removeTaskRegularStart());

    // task check
    if (!action.payload.id) {
      throw new Error('No task');
    }

    const tasksStructure = yield call(fetchRequest, `${API_URL}${API_PATH_TASKS_REGULAR}/${action.payload.id}`, 'DELETE');

    yield put(actions.removeTaskRegularSuccess());
    yield put(actions.getTasksServerSuccess(convertTasksStructure(tasksStructure.data)));

    toast(TEXTS[language].TASK_DELETED, toastOptionsSuccess);
  } catch (error) {
    toast(error.message, toastOptionsError);
    yield put(actions.removeTaskRegularFail(error));
  }
}

// / remove Delayed task

export function* removeTaskDelayedSaga(action) {
  try {
    yield put(actions.removeTaskDelayedStart());

    // task check
    if (!action.payload.id) {
      throw new Error('No task');
    }

    const tasksStructure = yield call(fetchRequest, `${API_URL}${API_PATH_TASKS_DELAYED}/${action.payload.id}`, 'DELETE');

    yield put(actions.removeTaskDelayedSuccess());
    yield put(actions.getTasksServerSuccess(convertTasksStructure(tasksStructure.data)));

    toast(TEXTS[language].TASK_DELETED, toastOptionsSuccess);
  } catch (error) {
    toast(error.message, toastOptionsError);
    yield put(actions.removeTaskDelayedFail(error));
  }
}

// / remove global task

export function* removeTaskGlobalSaga(action) {
  try {
    yield put(actions.removeTaskGlobalStart());

    // task check
    if (!action.payload.id) {
      throw new Error('No task');
    }

    const tasksStructure = yield call(fetchRequest, `${API_URL}${API_PATH_TASKS_GLOBAL}/${action.payload.id}`, 'DELETE');

    yield put(actions.removeTaskGlobalSuccess());
    yield put(actions.getTasksServerSuccess(convertTasksStructure(tasksStructure.data)));

    toast(TEXTS[language].GOAL_DELETED, toastOptionsSuccess);
  } catch (error) {
    toast(error.message, toastOptionsError);
    yield put(actions.removeTaskGlobalFail(error));
  }
}

export function* getTasksServerSaga() {
  try {
    yield put(actions.getTasksServerStart());

    const tasksStructure = yield call(fetchRequest, `${API_URL}${API_PATH_TASKS}`, 'GET');

    yield put(actions.getTasksServerSuccess(convertTasksStructure(tasksStructure.data)));
  } catch (error) {
    toast(error.message, toastOptionsError);
    yield put(actions.getTasksServerFail(error));
  }
}

export function* refreshTasksSaga() {
  try {
    const tasksStructure = yield call(fetchRequest, `${API_URL}${API_PATH_TASKS}`, 'GET');

    yield put(actions.getTasksServerSuccess(convertTasksStructure(tasksStructure.data)));

    return tasksStructure;
  } catch (error) {
    return error;
  }
}
